import type { EventWidget, EventWidgetData } from "@/api/bale.zod";
import type { DragMoveEvent } from "@/components/drag-columns/context/DragProvider";

import { useDeleteEventWidget, useUpdateEventWidgetLayout } from "@/api/bale";
import { uuidVersion } from "@/fp/uuidVersion";

import type { AdditionalWidget } from "../components/cards/AddEventWidget";

import { useEventWidgetsContext } from "../contexts/EventWidgetsContext";
import { useCurrentEventSuspense } from "./useCurrentEvent";

export type ActiveWidget = Pick<EventWidget, "row" | "column" | "id" | "widget_type"> & Partial<EventWidgetData>;

export function useEventWidgets() {
  const [draftLayout, setDraftLayout] = useEventWidgetsContext();
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });
  const updateWidgetLayout = useUpdateEventWidgetLayout({ mutation: { onSuccess: () => setDraftLayout(null) } });
  const deleteWidget = useDeleteEventWidget({
    mutation: { onSuccess: () => event.refetch() },
  });
  const layout = draftLayout ?? event.data.event_widgets;
  const canMove = !draftLayout || draftLayout.length === event.data.event_widgets.length;
  const isLayoutModified =
    draftLayout &&
    !event.data.event_widgets
      .toSorted((a, b) => a.column - b.column || a.row - b.row)
      .every(
        (widget, i) =>
          draftLayout[i] &&
          draftLayout[i].id === widget.id &&
          draftLayout[i].row === widget.row &&
          draftLayout[i].column === widget.column,
      );

  function moveWidget({ from, to }: DragMoveEvent) {
    setDraftLayout(
      layout
        .map(({ ...widget }) => {
          if (widget.column === from.column && widget.row === from.row) {
            widget.column = to.column;
            widget.row = to.row;
            return widget;
          }
          if (widget.column === from.column && widget.row > from.row) widget.row -= 1;
          if (widget.column === to.column && widget.row >= to.row) widget.row += 1;
          return widget;
        })
        .toSorted((a, b) => a.column - b.column || a.row - b.row),
    );
  }

  function addWidget(widgetType: AdditionalWidget) {
    setDraftLayout([
      ...layout,
      {
        // NOTE: server uses UUIDv7 for IDs, client is using UUIDv4, so we can tell them apart.
        // When there are client-side widgets, then we cannot support reordering things until
        // all are commited, as we can't save reordered client side widgets
        id: window.crypto.randomUUID(),
        row: Math.max(...layout.filter((item) => item.column === 1).map((item) => item.row + 1), 0),
        column: 1,
        widget_type: widgetType,
      },
    ]);
  }

  function replaceWidget(widgetId: string, widget: ActiveWidget) {
    void event.refetch();
    setDraftLayout(layout.map((item) => (item.id === widgetId ? widget : item)));
  }

  function removeWidget(widgetId: string) {
    if (uuidVersion(widgetId) === 7) deleteWidget.mutate({ widgetId });
    setDraftLayout((layout) => layout?.filter((widget) => widget.id !== widgetId) ?? null);
  }

  function saveLayout() {
    if (!draftLayout) return;
    updateWidgetLayout.mutate({
      eventId: event.data.event.id,
      data: { widget_layout: draftLayout.map(({ id, column, row }) => ({ id, column, row })) },
    });
  }

  function resetLayout() {
    setDraftLayout(null);
  }

  return {
    layout,
    isLayoutModified,
    moveWidget,
    canMove,
    saveLayout,
    resetLayout,
    addWidget,
    replaceWidget,
    removeWidget,
  };
}
